import {grappleAttackDisadvantages} from '../../../shared/rules/unarmedOptions.mjs';
import { hasActiveEffect } from './combatTimeline';
import { minimumFootprintDistanceFeet } from './pushGeometry';
import { isIncapacitated } from '../../../shared/rules/barbarianRage.mjs';
import { areAllied } from '../../../shared/creatureDisposition';

export const PRONE_DEFINITION_ID = 'condition:prone';
export const POISONED_DEFINITION_ID = 'condition:poisoned';
export const isProne = (combatState, combatantId) => hasActiveEffect(combatState, combatantId, PRONE_DEFINITION_ID);
export const isPoisoned = (combatState, combatantId) => hasActiveEffect(combatState, combatantId, POISONED_DEFINITION_ID);

export const createPoisonedEffect = ({ targetCombatantId, sourceCombatantId, sourceResolutionId, dc, round = 1 } = {}) => ({
  id: `poisoned:${sourceResolutionId || targetCombatantId}`,
  definitionId: POISONED_DEFINITION_ID, name: 'Poisoned', icon: '☠', userVisible: true,
  targetCombatantId, sourceCombatantId, sourceResolutionId, sourceFeature: 'rogue:cunning-strike:poison',
  stackKey: `${POISONED_DEFINITION_ID}:${targetCombatantId}`, stackPolicy: 'ignore', removeOnSourceRemoval: false,
  repeatSave: { timing: 'end-of-target-turn', ability: 'constitution', dc, onSuccess: 'remove-effect' },
  expiration: { type: 'rounds', expiresAtRound: round + 10, boundary: 'end' },
  duration: 'Up to 1 minute (10 rounds); repeat the CON save at the end of each turn.',
  description: 'Disadvantage on attack rolls and ability checks.',
});

export const getConditionAttackSources = ({ combatState, attackerId, targetId } = {}) => ({
  advantageSources: [...(hasActiveEffect(combatState, targetId, 'condition:restrained') ? ['Target: Restrained'] : []),...(hasActiveEffect(combatState,targetId,'condition:stunned')?['Target: Stunned']:[]),
    ...(hasActiveEffect(combatState, targetId, 'condition:blinded') ? ['Target: Blinded'] : []),
    ...(hasActiveEffect(combatState, targetId, 'condition:unconscious') ? ['Target: Unconscious'] : [])],
  disadvantageSources: [
    ...grappleAttackDisadvantages(combatState,attackerId,targetId),
    ...(isPoisoned(combatState, attackerId) ? ['Condition: Poisoned'] : []),
    ...(hasActiveEffect(combatState, attackerId, 'condition:restrained') ? ['Condition: Restrained'] : []),
    ...(hasActiveEffect(combatState, attackerId, 'condition:blinded') ? ['Condition: Blinded'] : []),
  ],
});

/** Feet between attacker and target from the map (or a stated proximity), or null when unknown. */
export const attackDistanceFeet = ({ attackerId, targetId, attacker, target, spatialContext, proximity } = {}) => {
  if (proximity != null) return proximity === 'within-5' ? 5 : 10;
  const tokens = spatialContext?.tokens || spatialContext?.map?.tokens || {};
  return minimumFootprintDistanceFeet({
    sourceToken: tokens[attackerId], sourceSize: attacker?.size || tokens[attackerId]?.size,
    targetToken: tokens[targetId], targetSize: target?.size || target?.creatureSize || tokens[targetId]?.size,
    map: spatialContext?.map,
  });
};

/** Pack Tactics: an ally of the attacker within 5 feet of the target who isn't Incapacitated. Needs both tokens on the map. */
export const hasPackAlly = ({ attackerId, targetId, attacker, target, combatState, spatialContext } = {}) => {
  const tokens = spatialContext?.tokens || spatialContext?.map?.tokens || {};
  return (combatState?.participants || []).some((participant) => {
    const id = String(participant?.characterId ?? participant?.enemyId ?? '');
    if (!id || id === String(attackerId) || id === String(targetId) || !tokens[id]) return false;
    const ally = { ...participant, ...(spatialContext?.entities?.[id] || {}) };
    if (!areAllied(ally, attacker) || isIncapacitated({ ...ally, combatantId: id }, combatState)) return false;
    const distance = attackDistanceFeet({ attackerId: id, targetId, attacker: ally, target, spatialContext });
    return distance != null && distance <= 5;
  });
};

export const createProneEffect = ({ targetCombatantId, sourceCombatantId, sourceResolutionId, sourceFeature } = {}) => ({
  id: `prone:${sourceResolutionId || targetCombatantId}`,
  definitionId: PRONE_DEFINITION_ID,
  name: 'Prone', icon: '↘', userVisible: true,
  targetCombatantId,
  ...(sourceCombatantId ? { sourceCombatantId } : {}),
  ...(sourceResolutionId ? { sourceResolutionId } : {}),
  ...(sourceFeature ? { sourceFeature } : {}),
  stackKey: `${PRONE_DEFINITION_ID}:${targetCombatantId}`,
  stackPolicy: 'ignore', expiration: { type: 'manual' }, removeOnSourceRemoval: false,
  description: 'Prone. Stand Up costs half effective Speed.',
});

export const getStandUpCost = (effectiveSpeed) => Number(effectiveSpeed) > 0 ? Math.floor(Number(effectiveSpeed) / 2) : null;

export const getProneAttackSources = ({ combatState, attackerId, targetId, attacker, target, spatialContext, proximity } = {}) => {
  const advantageSources = [];
  const disadvantageSources = [];
  if (isProne(combatState, attackerId)) disadvantageSources.push('Condition: Prone');
  if (isProne(combatState, targetId)) {
    const distance = attackDistanceFeet({ attackerId, targetId, attacker, target, spatialContext, proximity });
    if (distance != null) (distance <= 5 ? advantageSources : disadvantageSources).push('Target: Prone');
  }
  return { advantageSources, disadvantageSources };
};

